import { IUpdateRequestParams } from './Operations';
import { escapeFilterValue } from '../../Shared/Util/StringUtils';

/**
 * Formats request parameters by converting the filter array to an OData-style query string.
 * This is a copy of the Analytics RequestFormatting to avoid impacting Analytics functionality.
 */
export const formatRequestParams = (paramsObj: IUpdateRequestParams) => {

    const obj = JSON.parse(JSON.stringify(paramsObj));

    const filterQueryList: any = [];

    obj.filter.filters.forEach((filter: any) => {
        // Skip empty filter values
        if (!isEmptyValue(filter.value)) {
            const queryString = getQueryString(filter);
            // Only add non-empty query strings to the filter list
            if (queryString) {
                filterQueryList.push(queryString);
            }
        }
    })

    // Only set the filters if there are valid filters to apply
    obj.filter.filters = filterQueryList.length > 0 ? filterQueryList.join(' and ') : '';

    return obj;

}

// Helper function to check if a value is empty
const isEmptyValue = (value: any): boolean => {
    return value === null || value === undefined || value === '';
};

// Helper function to check if value is already a full ISO timestamp (contains 'T' and ends with 'Z')
const isISOTimestamp = (value: string): boolean => {
    return typeof value === 'string' && value.includes('T') && value.endsWith('Z');
};


const getQueryString = (filter: any) => {
    // If the filter value is empty, return an empty string
    if (isEmptyValue(filter.value)) {
        return '';
    }
    
    switch (filter.operator) {
        case 'contains':
            // Escape the filter value to handle apostrophes
            const escapedContainsValue = escapeFilterValue(filter.value);
            return `search.ismatch('${escapedContainsValue}*','${filter.field}')`
        case 'eq':
            // Ensure value is an array before using forEach
            const eqValues = Array.isArray(filter.value) ? filter.value : [filter.value];
            let queryStringOr: string = '';
            eqValues.forEach((val: string, index: number) => {
                // Escape each value to handle apostrophes
                const escapedVal = escapeFilterValue(val);
                queryStringOr += `${filter.field} eq '${escapedVal}'${index < eqValues.length - 1 ? ' or ' : ''}`
            })
            return `(${queryStringOr})`;
        case 'array eq':
            // Ensure value is an array before using forEach
            const arrayEqValues = Array.isArray(filter.value) ? filter.value : [filter.value];
            let queryString: string = '';
            arrayEqValues.forEach((val: string, index: number) => {
                // Escape each value to handle apostrophes
                const escapedVal = escapeFilterValue(val);
                queryString += `t eq '${escapedVal}'${index < arrayEqValues.length - 1 ? ' or ' : ''}`
            })
            return `${filter.field}/any(t: ${queryString})`;
        case 'ge':
            // Handle date fields for Updates
            const isDateField = filter.field === "dateCreated" || filter.field === "dateModified" || filter.field === "notification/dateCreated";
            
            // If value is already a full ISO timestamp (from Doc Update Days filter), use it as-is
            // Otherwise, add T00:00:00Z for date-only values
            const greaterValue = isDateField && !isISOTimestamp(filter.value)
                ? `${filter.value}T00:00:00Z`
                : filter.value;
            return `${filter.field} ge ${greaterValue}`;
        case 'gt':
            // Handle date fields for Updates
            const isDateFieldgt = filter.field === "notification/dateCreated";
            
            // If value is already a full ISO timestamp (from Doc Update Days filter), use it as-is
            // Otherwise, add T00:00:00Z for date-only values
            const greaterValuegt = isDateFieldgt && !isISOTimestamp(filter.value)
                ? `${filter.value}T00:00:00Z`
                : filter.value;
            return `${filter.field} gt ${greaterValuegt}`;
        case 'le':
            // Handle date fields for Updates
            const isDateFieldLe = filter.field === "dateCreated" || filter.field === "dateModified" || filter.field === "notification/dateCreated";
            
            // If value is already a full ISO timestamp (from Doc Update Days filter), use it as-is
            // Otherwise, add T23:59:59Z for date-only values
            const lesserValue = isDateFieldLe && !isISOTimestamp(filter.value)
                ? `${filter.value}T23:59:59Z`
                : filter.value;
            return `${filter.field} le ${lesserValue}`;
        case 'lt':
            // Handle date fields for Updates
            const isDateFieldLt = filter.field === "dateCreated" || filter.field === "dateModified" || filter.field === "notification/dateCreated";
            
            // If value is already a full ISO timestamp (from Doc Update Days filter), use it as-is
            // Otherwise, add T00:00:00Z for date-only values
            const lesserValueLt = isDateFieldLt && !isISOTimestamp(filter.value)
                ? `${filter.value}T00:00:00Z`
                : filter.value;
            return `${filter.field} lt ${lesserValueLt}`;
        default:
            return ''
    }
}
